import GsuLoader from '@/components/GsuLoader'
import { PageMetaInfo } from '@/compositions/MetaInfo'
import { FragPageMetaInfo } from '@/compositions/MetaInfo/query'
import type { Props as PageComposerProps } from '@/compositions/PageComposer'
import { FragComposerSections } from '@/compositions/PageComposer/query'
import PageLayout from '@/compositions/PageLayout'
import { ModalProvider } from '@/context/modal'
import { queryGsuApi } from '@/service/query'
import { Page } from '@/shared/api-generated-types'
import type { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import { gql, useQuery } from 'urql'

// PageComposer dynamic import to prevent SSR hydration issues
const PageComposerWithLoader = dynamic<PageComposerProps>(() => import('@/compositions/PageComposer'), {
  ssr: false,
  loading: () => <GsuLoader consumeSpace />,
})

const QUERY_SERIES = gql`
  query Series($slug: String!) {
    ${FragComposerSections}
    ${FragPageMetaInfo}
    page: seriesPage(slug: $slug) {
      title
      id
      metaInfo {
        ...PageMetaInfo
      }
      sections {
        ...CoreSections
        ...RecursiveSections
      }
    }
  }
`

const Series: NextPage<{ slug: string }> = (props) => {
  const [{ data, fetching }] = useQuery({
    query: QUERY_SERIES,
    variables: { slug: props.slug },
    requestPolicy: 'cache-first',
    pause: !props?.slug,
  })

  const pageData: Page = data?.page || {}

  return (
    <PageLayout>
      <PageMetaInfo {...pageData.metaInfo} />
      <ModalProvider>
        <PageComposerWithLoader {...pageData} fetching={fetching} />
      </ModalProvider>
    </PageLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ locale = 'en-US', req, res, query: { slug } }) => {
  const [urqlState, statusCode] = await queryGsuApi(QUERY_SERIES, { slug }, req)

  if (!!statusCode) {
    console.log('Series page failed to retrieve details', statusCode)
    res.statusCode = statusCode
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ['badge', 'classes', 'common', 'partner'])),
      slug,
      urqlState,
    },
  }
}

export default Series
